import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { StateEvent, StateTransitionCondition } from '@sierralabs/workflow';

import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import transitionConditionPlugins from '../plugins/transition-conditions';
import { WorkflowTaskTypeEntity } from '../Workflow.interface';

const Styled = {
  Dialog: styled(Dialog)`
    .delete {
      color: red;
    }
  `,
  FormHelperText: styled(FormHelperText)`
    padding-left: 8px;
  `
};

const StateEventConditionDialog: React.FC<{
  event: StateEvent;
  type?: WorkflowTaskTypeEntity;
  condition?: StateTransitionCondition;
  open: boolean;
  onCancel?: () => void;
  onChange?: (condition: StateTransitionCondition) => void;
  onDelete?: (condition: StateTransitionCondition) => void;
}> = ({
  event,
  type,
  condition: initialCondition,
  open,
  onCancel,
  onChange,
  onDelete
}) => {
  const [condition, setCondition] = useState(
    initialCondition || { type: '', params: {} }
  );
  if (!condition.params) {
    condition.params = {};
  }
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const plugin = transitionConditionPlugins.find(
    plugin => plugin.type === condition.type
  );

  return (
    <Styled.Dialog open={open} onClose={onCancel}>
      <DialogTitle>Transition Condition</DialogTitle>
      <DialogContent>
        <Autocomplete
          fullWidth
          value={plugin || null}
          options={transitionConditionPlugins}
          filterOptions={options => {
            // NOTE: filter plugins through `filterOptions` so that any valid existing
            // plugin configuration will still appear and won't show 'plugin not found'
            return options.filter(
              option => !option.canConfigure || option.canConfigure(event)
            );
          }}
          getOptionLabel={option => option.label}
          getOptionSelected={(option, value) => option.type === value.type}
          onChange={(_, value) => {
            setCondition({ ...condition, type: value ? value.type : '' });
          }}
          renderInput={params => (
            <TextField
              {...params}
              autoFocus
              fullWidth
              size='small'
              variant='outlined'
              label='Conditions'
              placeholder='Select a condition'
            />
          )}
        />

        {!condition.type ? null : !plugin ? (
          <Box mt={2}>
            <Alert severity='error'>
              Transition condition "{condition.type}" plugin not found.
            </Alert>
          </Box>
        ) : (
          <div className='transition-condition'>
            <plugin.configurator
              taskType={type}
              event={event}
              condition={condition}
              onChange={condition => {
                setCondition(condition);
              }}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {initialCondition && (
          <Box flex={1}>
            <Button
              className='delete'
              onClick={() => setOpenConfirmDelete(true)}
            >
              Delete
            </Button>
          </Box>
        )}
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            onChange?.(condition);
          }}
        >
          Save
        </Button>
      </DialogActions>
      {openConfirmDelete && (
        <ConfirmDialog
          title='Delte Transition Condition'
          message='Are you sure you want to delete this transition condition?'
          open={openConfirmDelete}
          onCancel={() => setOpenConfirmDelete(false)}
          onConfirm={() => {
            setOpenConfirmDelete(false);
            onDelete?.(condition);
          }}
        />
      )}
    </Styled.Dialog>
  );
};

export default StateEventConditionDialog;
